(function cartPageControllerFactory(root) {
  function normalizeCartQuantity(value) {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return 0;
    }

    return Math.floor(numericValue);
  }

  function buildCartChangePayload(line, quantity) {
    const numericLine = Number(line);
    const safeLine = Number.isFinite(numericLine) && numericLine > 0 ? Math.floor(numericLine) : 1;

    return {
      line: safeLine,
      quantity: normalizeCartQuantity(quantity),
    };
  }

  function buildCartNotePayload(note) {
    return {
      note: String(note ?? ''),
    };
  }

  function buildShippingRatesSearchParams(address = {}) {
    const params = new URLSearchParams();
    const zip = String(address.zip ?? '').trim();
    const country = String(address.country ?? '').trim();
    const province = String(address.province ?? '').trim();

    if (zip) {
      params.set('shipping_address[zip]', zip);
    }

    if (country) {
      params.set('shipping_address[country]', country);
    }

    if (province) {
      params.set('shipping_address[province]', province);
    }

    return params.toString();
  }

  function normalizeShippingRatesResponse(data) {
    if (!data || !Array.isArray(data.shipping_rates)) {
      return [];
    }

    return data.shipping_rates;
  }

  function formatShippingRatePrice(rate, locale = 'en-US') {
    const rawPrice = rate && typeof rate.price !== 'undefined' ? String(rate.price) : '';
    const numericPrice = Number(rawPrice);

    if (!Number.isFinite(numericPrice)) {
      return rawPrice;
    }

    const currency = rate.currency || root.Shopify?.currency?.active || 'USD';

    if (root.Intl && typeof root.Intl.NumberFormat === 'function') {
      return new root.Intl.NumberFormat(locale, {
        currency,
        style: 'currency',
      }).format(numericPrice);
    }

    return `$${numericPrice.toFixed(2)}`;
  }

  function buildCartApiPath(path) {
    const routeRoot = root.Shopify?.routes?.root || '/';
    const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    const normalizedPath = String(path || '').replace(/^\//, '');

    return `${normalizedRoot}${normalizedPath}`;
  }

  function buildDiscountRedirectUrl(code) {
    const discountPath = `/discount/${encodeURIComponent(String(code || '').trim())}`;
    const redirectPath = buildCartApiPath('/cart');

    return buildCartApiPath(`${discountPath}?redirect=${encodeURIComponent(redirectPath)}`);
  }

  function formatPaddedQuantity(quantity) {
    return String(normalizeCartQuantity(quantity)).padStart(2, '0');
  }

  function parseCartError(error) {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    return error.description || error.message || '';
  }

  function getCartActionMessage(action, messages = {}) {
    if (action === 'add') {
      return messages.adding || '';
    }

    if (action === 'remove') {
      return messages.removing || '';
    }

    if (action === 'update') {
      return messages.updating || '';
    }

    return '';
  }

  function updateHeaderCartCount(cart, template = root.theme?.cartCountTemplate || '') {
    if (!root.document || !cart || typeof cart.item_count === 'undefined') {
      return;
    }

    const count = String(cart.item_count);
    const label = String(template || '').replace('__count__', count);

    root.document.querySelectorAll('.site-header__cart-count').forEach((countBadge) => {
      countBadge.textContent = count;
      if (label) {
        countBadge.setAttribute('aria-label', label);
      }
    });
  }

  function dispatchCartUpdated(cart) {
    if (
      !root.document
      || !cart
      || typeof cart.item_count === 'undefined'
      || typeof root.document.dispatchEvent !== 'function'
      || typeof root.CustomEvent !== 'function'
    ) {
      return;
    }

    root.document.dispatchEvent(new root.CustomEvent('cart:updated', {
      detail: {
        cart,
        openDrawer: false,
        source: 'cart-page',
      },
    }));
  }

  function registerAlpine() {
    if (!root.document || !root.Alpine) {
      return;
    }

    root.Alpine.data('cartPage', (config = {}) => ({
      sectionId: config.sectionId || '',
      errors: config.errors || {},
      messages: config.messages || {},
      updatingLine: null,
      removingLine: null,
      editingLine: null,
      addingVariantId: null,
      errorMessage: '',
      cartActionMessage: '',
      noteValue: config.initialNote || '',
      discountValue: '',
      discountMessage: '',
      discountStatus: '',
      discountLoading: false,
      shippingRates: [],
      shippingMessage: '',
      shippingLoading: false,
      shippingRequestKey: '',
      shippingOpen: false,
      noteOpen: false,
      discountOpen: false,
      setupOpen: true,
      refreshController: null,
      refreshSequence: 0,

      formatPaddedQuantity,

      init() {
        this.$nextTick(() => root.cartInventory?.prioritizeCartLineItems(this.$root));
      },

      destroy() {
        this.refreshController?.abort();
      },

      announceCartError(message) {
        if (!message || typeof root.dispatchEvent !== 'function' || typeof root.CustomEvent !== 'function') {
          return;
        }

        root.dispatchEvent(new root.CustomEvent('cart-page:error', {
          detail: { message },
        }));
      },

      get hasError() {
        return this.errorMessage !== '';
      },

      get shippingStatusMessage() {
        if (this.shippingLoading) {
          return this.messages.shippingCalculating || '';
        }

        return this.shippingMessage;
      },

      get shippingAddress() {
        return {
          country: this.$refs.shippingCountry?.value || '',
          province: this.$refs.shippingState?.value || '',
          zip: this.$refs.shippingPostalCode?.value || '',
        };
      },

      formatShippingRatePrice(rate) {
        return formatShippingRatePrice(rate, root.navigator?.language || 'en-US');
      },

      syncCart(cart) {
        updateHeaderCartCount(cart, this.messages.cartCountTemplate || root.theme?.cartCountTemplate || '');
        dispatchCartUpdated(cart);
      },

      async handleCartUpdated(event) {
        if (event?.detail?.source === 'cart-page') {
          return;
        }

        await this.refreshSection();
      },

      async requestCart(url, payload, method = 'POST') {
        if (!root.fetch) {
          return null;
        }

        const requestOptions = {
          method,
          headers: {
            Accept: 'application/json',
          },
        };

        if (method !== 'GET') {
          requestOptions.headers['Content-Type'] = 'application/json';
          requestOptions.body = JSON.stringify(payload);
        }

        const response = await root.fetch(buildCartApiPath(url), requestOptions);

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(parseCartError(data) || response.statusText);
        }

        this.syncCart(data);

        return data;
      },

      async refreshSection() {
        if (!this.sectionId || !root.fetch || !root.DOMParser || !root.location || !this.$root) {
          if (root.location && typeof root.location.reload === 'function') {
            root.location.reload();
          }
          return;
        }

        const requestSequence = this.refreshSequence + 1;
        this.refreshSequence = requestSequence;
        this.refreshController?.abort();
        const requestController = typeof root.AbortController === 'function'
          ? new root.AbortController()
          : null;
        this.refreshController = requestController;

        try {
          const url = new URL(root.location.href);
          url.searchParams.set('section_id', this.sectionId);

          const requestOptions = {
            headers: {
              Accept: 'text/html',
            },
          };
          if (requestController) {
            requestOptions.signal = requestController.signal;
          }
          const response = await root.fetch(url.toString(), requestOptions);

          if (!response.ok) {
            if (requestSequence === this.refreshSequence && root.location && typeof root.location.reload === 'function') {
              root.location.reload();
            }
            return;
          }

          const html = await response.text();
          const documentFragment = new root.DOMParser().parseFromString(html, 'text/html');
          const nextRoot = documentFragment.querySelector(`[data-cart-page-section="${this.sectionId}"]`);

          if (requestSequence !== this.refreshSequence || requestController?.signal.aborted) {
            return;
          }

          if (!nextRoot) {
            if (root.location && typeof root.location.reload === 'function') {
              root.location.reload();
            }
            return;
          }

          root.cartInventory?.prioritizeCartLineItems(nextRoot);
          this.$root.replaceWith(nextRoot);

          if (root.Alpine && typeof root.Alpine.initTree === 'function') {
            root.Alpine.initTree(nextRoot);
          }

          if (typeof root.configureShippingProtection === 'function') {
            root.configureShippingProtection();
          }
        } catch (error) {
          if (
            error?.name !== 'AbortError'
            && requestSequence === this.refreshSequence
            && root.location
            && typeof root.location.reload === 'function'
          ) {
            root.location.reload();
          }
        } finally {
          if (requestSequence === this.refreshSequence) {
            this.refreshController = null;
          }
        }
      },

      async updateLine(line, quantity, lineKey = '') {
        if (this.updatingLine !== null || this.removingLine !== null) {
          return;
        }

        this.errorMessage = '';
        this.updatingLine = Number(line) || 1;
        if (normalizeCartQuantity(quantity) === 0) {
          this.cartActionMessage = getCartActionMessage('remove', this.messages);
        } else {
          this.cartActionMessage = getCartActionMessage('update', this.messages);
        }

        try {
          if (!root.cartInventory) {
            throw new Error(this.errors.update || '');
          }

          const result = await root.cartInventory.changeLine(lineKey || line, quantity);
          this.syncCart(result.cart);
          await this.refreshSection();
          this.announceCartError(result.message || '');
        } catch (error) {
          const errorMessage = parseCartError(error) || this.errors.update || '';
          if (error?.cart) {
            this.syncCart(error.cart);
          }
          await this.refreshSection();
          this.announceCartError(errorMessage);
        } finally {
          this.updatingLine = null;
          this.cartActionMessage = '';
        }
      },

      async commitQuantity(event, line, currentQuantity, maximumQuantity = null, lineKey = '') {
        const input = event?.currentTarget;
        const previousQuantity = Math.max(normalizeCartQuantity(currentQuantity), 1);
        const requestedQuantity = normalizeCartQuantity(input?.value);
        const nextQuantity = root.cartInventory
          ? root.cartInventory.clampLineQuantity(requestedQuantity, maximumQuantity, previousQuantity)
          : requestedQuantity || previousQuantity;

        if (input) {
          input.value = formatPaddedQuantity(nextQuantity);
        }

        if (nextQuantity === previousQuantity) {
          return;
        }

        await this.updateLine(line, nextQuantity, lineKey);
      },

      async removeLine(line, lineKey = '') {
        if (this.updatingLine !== null || this.removingLine !== null) {
          return;
        }

        this.removingLine = Number(line) || 1;
        const reducedMotion = root.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
        if (!reducedMotion) {
          await new Promise((resolve) => root.setTimeout(resolve, 180));
        }

        this.removingLine = null;
        await this.updateLine(line, 0, lineKey);
      },

      async estimateShipping() {
        const address = this.shippingAddress;
        const params = buildShippingRatesSearchParams(address);

        this.shippingRates = [];
        this.shippingMessage = '';

        if (!address.country || !address.zip) {
          this.shippingMessage = this.messages.shippingEnterDestination || '';
          return;
        }

        if (!root.fetch) {
          this.shippingMessage = this.messages.shippingError || this.errors.update || '';
          return;
        }

        this.shippingLoading = true;
        this.shippingRequestKey = params;

        try {
          const prepareUrl = `${buildCartApiPath('/cart/prepare_shipping_rates.json')}?${params}`;
          const ratesUrl = `${buildCartApiPath('/cart/async_shipping_rates.json')}?${params}`;

          const prepareResponse = await root.fetch(prepareUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
            },
          });

          if (!prepareResponse.ok) {
            throw new Error(prepareResponse.statusText);
          }

          let rates = [];

          for (let attempt = 0; attempt < 6; attempt += 1) {
            if (attempt > 0) {
              const delay = root.setTimeout || setTimeout;
              await new Promise((resolve) => delay(resolve, 500));
            }

            const response = await root.fetch(ratesUrl, {
              headers: {
                Accept: 'application/json',
              },
            });

            if (!response.ok) {
              throw new Error(response.statusText);
            }

            const data = await response.json().catch(() => null);

            if (data === null) {
              continue;
            }

            rates = normalizeShippingRatesResponse(data);
            break;
          }

          if (this.shippingRequestKey !== params) {
            return;
          }

          this.shippingRates = rates;
          this.shippingMessage = rates.length > 0 ? '' : this.messages.shippingNoRates || '';
        } catch (error) {
          if (this.shippingRequestKey === params) {
            this.shippingMessage = parseCartError(error) || this.messages.shippingError || this.errors.update || '';
          }
        } finally {
          if (this.shippingRequestKey === params) {
            this.shippingLoading = false;
          }
        }
      },

      async saveNote(note) {
        this.errorMessage = '';
        this.cartActionMessage = getCartActionMessage('update', this.messages);

        try {
          await this.requestCart('/cart/update.js', buildCartNotePayload(note));
          await this.refreshSection();
        } catch (error) {
          this.errorMessage = parseCartError(error) || this.errors.note || '';
        } finally {
          this.cartActionMessage = '';
        }
      },

      async applyDiscount(code) {
        const discountCode = String(code || '').trim();

        if (!discountCode || !root.location) {
          return;
        }

        // The old /discount/CODE redirect reloads the page and stays silent on
        // invalid codes. The Cart API reports whether the code applies, so ask
        // it first and only fall back to the redirect when it cannot answer.
        if (!root.fetch) {
          root.location.href = buildDiscountRedirectUrl(discountCode);
          return;
        }

        this.discountLoading = true;
        this.discountMessage = '';
        this.discountStatus = '';

        try {
          const response = await root.fetch(buildCartApiPath('/cart/update.js'), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ discount: discountCode }),
          });
          const cart = await response.json().catch(() => null);

          if (!response.ok || !cart || !Array.isArray(cart.discount_codes)) {
            root.location.href = buildDiscountRedirectUrl(discountCode);
            return;
          }

          const match = cart.discount_codes.find(
            (entry) => String(entry?.code || '').toLowerCase() === discountCode.toLowerCase(),
          );

          if (!match || !match.applicable) {
            this.discountStatus = 'error';
            this.discountMessage = this.messages.discountInvalid || '';
            return;
          }

          this.discountStatus = 'success';
          this.discountMessage = this.messages.discountApplied || '';
          this.syncCart(cart);
          await this.refreshSection();
        } catch (error) {
          this.discountStatus = 'error';
          this.discountMessage = this.messages.discountError || this.errors.update || '';
        } finally {
          this.discountLoading = false;
        }
      },

      async addAddon(event) {
        const button = event.currentTarget;
        const variantId = button?.dataset?.cartAddonVariantId;
        const quantity = button?.dataset?.cartAddonQuantity || 1;
        const maxQuantity = button?.dataset?.cartAddonQuantityMax || null;
        const minimum = button?.dataset?.cartAddonQuantityMin || 1;
        const increment = button?.dataset?.cartAddonQuantityIncrement || 1;

        if (!variantId || !root.cartInventory) {
          return;
        }

        this.errorMessage = '';
        this.addingVariantId = String(variantId);
        this.cartActionMessage = getCartActionMessage('add', this.messages);

        try {
          const result = await root.cartInventory.addVariant({
            id: Number(variantId),
            quantity: Math.max(Number(quantity) || 1, 1),
            maxQuantity,
            minimum,
            increment,
          });
          this.syncCart(result.cart);
          await this.refreshSection();
          this.announceCartError(result.message || '');
        } catch (error) {
          const errorMessage = parseCartError(error) || this.errors.add || '';
          if (error?.cart) {
            this.syncCart(error.cart);
            await this.refreshSection();
          }
          this.announceCartError(errorMessage);
        } finally {
          this.addingVariantId = null;
          this.cartActionMessage = '';
        }
      },
    }));
  }

  if (root.document) {
    root.document.addEventListener('alpine:init', registerAlpine);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildCartApiPath,
      buildDiscountRedirectUrl,
      buildShippingRatesSearchParams,
      buildCartChangePayload,
      buildCartNotePayload,
      formatShippingRatePrice,
      formatPaddedQuantity,
      getCartActionMessage,
      normalizeShippingRatesResponse,
      normalizeCartQuantity,
      updateHeaderCartCount,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
